/**
 * TaskFlow · ALTARODA — PWA helpers (Bloco 4 PoC)
 * - Register service worker at /sw.js (only in production build or when
 *   REACT_APP_ENABLE_SW=true so we don't break dev hot reload).
 * - Capture the `beforeinstallprompt` event to allow a custom install button.
 * - Detect new SW available and expose a callback for the UI banner.
 */

let deferredInstallPrompt = null;
let newWorker = null;
let installedFlag = false;
const listeners = { update: [], installReady: [], installed: [] };

export function onUpdateAvailable(cb) {
  listeners.update.push(cb);
  return () => {
    listeners.update = listeners.update.filter((x) => x !== cb);
  };
}

export function onInstallReady(cb) {
  listeners.installReady.push(cb);
  return () => {
    listeners.installReady = listeners.installReady.filter((x) => x !== cb);
  };
}

export function onInstalled(cb) {
  listeners.installed.push(cb);
  return () => {
    listeners.installed = listeners.installed.filter((x) => x !== cb);
  };
}

export function isInstalled() {
  return (
    installedFlag ||
    (typeof window !== "undefined" &&
      (window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator && window.navigator.standalone === true)))
  );
}

export function canInstall() {
  return !!deferredInstallPrompt;
}

export async function promptInstall() {
  if (!deferredInstallPrompt) return { outcome: "unavailable" };
  try {
    deferredInstallPrompt.prompt();
    const choice = await deferredInstallPrompt.userChoice;
    if (choice && choice.outcome === "accepted") {
      deferredInstallPrompt = null;
    }
    return choice;
  } catch (e) {
    return { outcome: "error", error: e };
  }
}

export function skipWaitingAndReload() {
  if (navigator.serviceWorker && navigator.serviceWorker.controller && newWorker) {
    newWorker.postMessage({ type: "SKIP_WAITING" });
    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  } else {
    window.location.reload();
  }
}

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  // Skip SW in webpack-dev-server unless explicitly enabled.
  const enabled =
    process.env.NODE_ENV === "production" ||
    process.env.REACT_APP_ENABLE_SW === "true";
  if (!enabled) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (
              nw.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              newWorker = nw;
              listeners.update.forEach((cb) => cb());
            }
          });
        });
      })
      .catch((e) => console.warn("SW register failed", e));
  });

  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    listeners.installReady.forEach((cb) => cb());
  });
  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    installedFlag = true;
    listeners.installed.forEach((cb) => cb());
  });
}

// -------- Push helpers --------
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
}

export async function getPushSubscription() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function subscribePush(publicKey) {
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
}

export async function unsubscribePush() {
  const sub = await getPushSubscription();
  if (!sub) return null;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  return endpoint;
}
